import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { useSwitchOr } from "@/hooks/access/useSwitchOr";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useEntityAccessOptions } from "@/hooks/user-management/useUserManagementQueries";
import { useUserHeldRoles, useUserRecord } from "@/hooks/user-management/useUserRecord";
import { usePersonCompanies } from "@/hooks/user-management/useUserRolesScope";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { showRecordOpenFailedAlert } from "@/utils/recordOpenFailedAlert";
import { RoleDetailSkeleton } from "@/components/features/RolesPermissions/RoleDetailSkeleton/RoleDetailSkeleton";
import { UserRecordProvider } from "@/components/features/user-management/UserRecord/UserRecordContext";
import { UserRecordShell } from "@/components/features/user-management/UserRecord/UserRecordShell";
import {
  USER_OPEN_FAILED_MESSAGE,
  USER_OPEN_FAILED_TITLE,
} from "@/components/features/user-management/UserRecord/userRecordCopy";
import {
  displayNameOf,
  personEntityNames,
} from "@/components/features/user-management/UserRecord/userRecordFormat";
import {
  DEFAULT_USER_RECORD_TAB,
  isUserRecordTab,
} from "@/components/features/user-management/UserRecord/userRecordTabs";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";

const userRecordSearchSchema = tableSearchSchema.extend({
  tab: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/user-management/$userId")({
  component: UserRecordPage,
  validateSearch: userRecordSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function UserRecordPage() {
  const { userId } = useParams({ from: "/_authenticated/user-management/$userId" });
  const navigate = useNavigate();
  const { hasAccess } = usePermissions();
  const { edit: legacyUserEdit } = hasAccess({ module: MODULE_KEYS_MAP.userManagement });
  // The record's writes ask user_management:edit, as the users list's row menu does; an Entity
  // Admin's bypass counts, as it does on the server.
  const canWriteUsers = useSwitchOr("user_management", "edit", !!legacyUserEdit);
  const { userId: currentUserId } = useActiveSession();

  const { activeTab: rawActiveTab, setActiveTab } = usePersistedTabs({
    defaultTab: DEFAULT_USER_RECORD_TAB,
  });
  const activeTab = isUserRecordTab(rawActiveTab) ? rawActiveTab : DEFAULT_USER_RECORD_TAB;

  const { data: user, isPending, refetch } = useUserRecord(userId);
  const { data: heldRoles, isError: isRolesError } = useUserHeldRoles(userId);
  const { data: entityOptions } = useEntityAccessOptions();
  const { data: personCompanies } = usePersonCompanies(userId);
  const roles = useMemo(
    () =>
      heldRoles
        ? heldRoles
            .filter((role) => !role.ended)
            .sort((a, b) => Number(a.temporary) - Number(b.temporary))
        : null,
    [heldRoles],
  );

  const entityNames = useMemo(
    () => personEntityNames(personCompanies ?? [], entityOptions ?? []),
    [personCompanies, entityOptions],
  );

  const displayName = user ? displayNameOf(user) : "";
  const breadcrumbItems = useMemo(
    () => [
      {
        title: "User Management",
        href: "/user-management",
        icon: getRouteIcon(MODULE_KEYS_MAP.userManagement, 16),
      },
      {
        title: displayName || (isPending ? "Loading..." : "Person not found"),
        href: `/user-management/${userId}`,
      },
    ],
    [displayName, isPending, userId],
  );
  useBreadcrumb(breadcrumbItems, { listView: "List View" });

  // A person who can't be opened leaves the admin on the list with a way back, never on an
  // empty page (Brain R&P §26).
  const failedToOpen = !isPending && !user;
  const redirectedUserId = useRef<string | null>(null);
  useEffect(() => {
    if (!failedToOpen || redirectedUserId.current === userId) return;
    redirectedUserId.current = userId;
    navigate({ to: "/user-management", replace: true });
    showRecordOpenFailedAlert({
      id: `user-open-failed-${userId}`,
      title: USER_OPEN_FAILED_TITLE,
      message: USER_OPEN_FAILED_MESSAGE,
      onRetry: () => navigate({ to: "/user-management/$userId", params: { userId } }),
    });
  }, [failedToOpen, userId, navigate]);

  if (isPending || !user) return <RoleDetailSkeleton />;

  return (
    <UserRecordProvider
      value={{
        user,
        displayName,
        roles,
        isRolesError,
        entityNames,
        entities: (user.entityIds ?? []).map((id) => entityNames[id]).filter(Boolean),
        isSelf: currentUserId != null && String(currentUserId) === user.id,
        canEdit: canWriteUsers,
        activeTab,
        setActiveTab,
        refresh: refetch,
      }}
    >
      <UserRecordShell />
    </UserRecordProvider>
  );
}
